import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import type { Appearance } from "odyssey-shared";
import { logical } from "../../ui/kit/index.ts";
import { LazyImages } from "./images.ts";
import type { LookPreviewSource } from "./look-figure.tsx";
import { paintPreview } from "./paint.ts";
import { previewPlan, previewStage, type PreviewStage } from "./plan.ts";

/**
 * A character's standing look, small (ADR-094 decision 27: "Character select draws each character's standing look
 * beside its name the same way"): the preview's plan and painter, facing front, one pixel of art to one logical
 * pixel, with no turn row. Every figure of one list shares one image store, so a sheet two characters wear is asked
 * for once, and only the sheets of the looks shown are asked for. A look is drawn only once all its sheets have
 * arrived.
 */

/**
 * One image store for the figures of a list, and a count of the images that have arrived, which changes each time
 * one does so the figures paint again.
 *
 * @param source The preview's source, or null while it loads or when it did not load.
 * @returns The store, null without a source, and the count.
 */
export function useSharedImages(source: LookPreviewSource | null): { readonly images: LazyImages | null; readonly arrivals: number } {
  const [arrivals, setArrivals] = useState(0);
  const images = useMemo(() => (source === null ? null : new LazyImages(source.base, () => setArrivals((count) => count + 1))), [source?.base]);
  return { images, arrivals };
}

/**
 * The stage a standing figure is drawn on, the same for every look, so a row can be sized before any sheet arrives.
 *
 * @param source The preview's source, or null.
 * @returns The stage, or null without a source or when the layout has no standing strip.
 */
export function standingStage(source: LookPreviewSource | null): PreviewStage | null {
  return source === null ? null : previewStage(source.manifest.layout);
}

/**
 * The standing look.
 *
 * @param props The `source`, the shared `images` and their `arrivals` count, the `look`, and the accessible `label`.
 * @returns The canvas, its stage's size in logical pixels, or nothing when the layout has no standing strip.
 * @example
 * <StandingLook source={source} images={images} arrivals={arrivals} look={character.look} label="Kaelen Ashgrove" />
 */
export function StandingLook({ source, images, arrivals, look, label }: { readonly source: LookPreviewSource; readonly images: LazyImages; readonly arrivals: number; readonly look: Appearance; readonly label: string }): ReactElement | null {
  const canvas = useRef<HTMLCanvasElement>(null);
  const plan = useMemo(() => previewPlan(source.looks, source.manifest, look, "down"), [source, look.skin, look.hair, look.clothes]);
  const [drawn, setDrawn] = useState(0);

  useEffect(() => {
    const context = canvas.current?.getContext("2d") ?? null;
    if (context === null || plan === null) {
      return;
    }
    setDrawn(paintPreview(context, plan, images));
  }, [plan, images, arrivals]);

  if (plan === null) {
    return null;
  }
  return (
    <canvas
      ref={canvas}
      className="ot-look__canvas"
      role="img"
      aria-label={label}
      data-look="standing"
      data-sheets={plan.sheets.map((sheet) => sheet.id).join(" ")}
      data-drawn={drawn}
      width={plan.stage.width}
      height={plan.stage.height}
      style={{ display: "block", width: logical(plan.stage.width), height: logical(plan.stage.height) }}
    />
  );
}
